/**
 * ATC-SIM command grammar (https://atc-sim.com), as the user specified it:
 *
 *   ID C 035        heading 035 (three digits, leading zeros required)   [L|R] forces turn direction
 *   ID C 3          cleared to 3 000 ft (one or two digits × 1 000)      [X|EX] expedite
 *   ID C OBK        direct to fix/VOR OBK                                 [L|R]
 *   ID L 27R        cleared to land runway 27R
 *   ID S 210        speed 210 kt
 *   ID H OBK        hold at OBK
 *   ID T            cleared for take-off
 *   ID W | ID LW    line up and wait
 *   ID A            abort take-off / go around
 *   ID I 12         cleared ILS approach runway 12 (no landing clearance; L clears to land)
 *   ID RN           resume own navigation (rejoin the flight plan)
 *   ID CT           contact departure (early handoff)
 *   ID CTW          contact tower in an Academy approach lesson
 *   ID RC           radar contact / roger: answer a call without an instruction (handled by Sim.command)
 *   ID C 19 C FONTI T   chained
 *
 * System commands: SCALE, STATS, QUIT/EXIT, and ours: PAUSE, SPEED n, REALISM low|mid|high,
 * AIRPORT icao, HELP. Parsing is pure; validation against the aircraft happens in pilot.ts.
 */
import type { TurnDirection } from '@atc/core';

export type Instruction =
  | { kind: 'altitude'; altitudeFt: number; expedite: boolean }
  | { kind: 'heading'; headingDeg: number; turn?: TurnDirection }
  | { kind: 'direct'; fix: string; turn?: TurnDirection }
  | { kind: 'land'; runway: string }
  | { kind: 'speed'; speedKt: number }
  | { kind: 'hold'; fix: string }
  | { kind: 'takeoff' }
  | { kind: 'lineUp' }
  | { kind: 'abort' }
  | { kind: 'approach'; runway: string }
  | { kind: 'resumeNav' }
  | { kind: 'contact' };

export type SystemCommand =
  | { kind: 'scale' } | { kind: 'stats' } | { kind: 'quit' } | { kind: 'pause' } | { kind: 'help' }
  | { kind: 'timeScale'; factor: number }
  | { kind: 'realism'; level: 'low' | 'mid' | 'high' }
  | { kind: 'airport'; icao: string }
  /** Display commands, handled by the game page. */
  | { kind: 'view'; layout: 'radar' | 'split' | 'tower' | 'pip' }
  | { kind: 'fullscreen' }
  | { kind: 'brief' }
  /** STARs and approaches on the scope; without a mode the game cycles in use → all → off. */
  | { kind: 'procedures'; mode?: 'off' | 'active' | 'all' }
  | { kind: 'trail' };

export type Command =
  | { type: 'aircraft'; callsign: string; instructions: Instruction[] }
  | { type: 'system'; command: SystemCommand };

export type ParseResult = { ok: true; command: Command } | { ok: false; message: string };

const RUNWAY = /^\d{1,2}[LRC]?$/;
const FIX = /^[A-Z][A-Z0-9_]{1,7}$/;
const KEYWORDS = new Set(['C', 'L', 'S', 'H', 'T', 'W', 'LW', 'A', 'I', 'RN', 'CT', 'RC']);

export function parseCommand(line: string): ParseResult {
  const toks = line.trim().toUpperCase().split(/\s+/).filter(Boolean);
  if (toks.length === 0) return { ok: false, message: 'Enter a command: ID C 035, ID L 28R, ID T …' };
  const head = toks[0]!;

  switch (head) {
    case 'SCALE': return { ok: true, command: { type: 'system', command: { kind: 'scale' } } };
    case 'STATS': return { ok: true, command: { type: 'system', command: { kind: 'stats' } } };
    case 'QUIT': case 'EXIT': return { ok: true, command: { type: 'system', command: { kind: 'quit' } } };
    case 'PAUSE': return { ok: true, command: { type: 'system', command: { kind: 'pause' } } };
    case 'HELP': case '?': return { ok: true, command: { type: 'system', command: { kind: 'help' } } };
    case 'SPEED': case 'RATE': {
      const f = Number(toks[1]);
      if (!Number.isFinite(f) || f < 0 || f > 16) return { ok: false, message: 'SPEED needs a factor 0-16, e.g. SPEED 4' };
      return { ok: true, command: { type: 'system', command: { kind: 'timeScale', factor: f } } };
    }
    case 'REALISM': {
      const l = toks[1]?.toLowerCase();
      if (l !== 'low' && l !== 'mid' && l !== 'high') return { ok: false, message: 'REALISM low | mid | high' };
      return { ok: true, command: { type: 'system', command: { kind: 'realism', level: l } } };
    }
    case 'AIRPORT': {
      if (!toks[1] || !/^[A-Z]{4}$/.test(toks[1])) return { ok: false, message: 'AIRPORT needs an ICAO code, e.g. AIRPORT LLBG' };
      return { ok: true, command: { type: 'system', command: { kind: 'airport', icao: toks[1] } } };
    }
    case 'VIEW': {
      const v = toks[1]?.toLowerCase();
      if (v !== 'radar' && v !== 'split' && v !== 'tower' && v !== 'pip') return { ok: false, message: 'VIEW radar | split | tower | pip' };
      return { ok: true, command: { type: 'system', command: { kind: 'view', layout: v } } };
    }
    case 'FULLSCREEN': return { ok: true, command: { type: 'system', command: { kind: 'fullscreen' } } };
    case 'BRIEF': case 'BRIEFING': return { ok: true, command: { type: 'system', command: { kind: 'brief' } } };
    case 'PROC': case 'PROCS': case 'PROCEDURES': {
      const m = toks[1]?.toLowerCase();
      if (m === undefined) return { ok: true, command: { type: 'system', command: { kind: 'procedures' } } };
      if (m !== 'off' && m !== 'active' && m !== 'all') return { ok: false, message: 'PROC off | active | all' };
      return { ok: true, command: { type: 'system', command: { kind: 'procedures', mode: m } } };
    }
    case 'TRAIL': return { ok: true, command: { type: 'system', command: { kind: 'trail' } } };
  }

  if (!/^[A-Z0-9]{2,8}$/.test(head) || KEYWORDS.has(head)) return { ok: false, message: `"${head}" is not a flight ID` };
  if (toks.length === 1) return { ok: false, message: `${head}: no command given` };

  const instructions: Instruction[] = [];
  let i = 1;
  const peek = (k = 0) => toks[i + k];
  const isTurn = (t: string | undefined): t is TurnDirection => t === 'L' || t === 'R';
  // "L" is a landing command when a runway follows, else a turn direction after C.
  const turnSuffix = (): TurnDirection | undefined => {
    const t = peek();
    if (!isTurn(t)) return undefined;
    if (t === 'L' && peek(1) !== undefined && RUNWAY.test(peek(1)!)) return undefined;
    i++;
    return t;
  };

  while (i < toks.length) {
    const kw = toks[i++]!;
    switch (kw) {
      case 'C': {
        const arg = toks[i++];
        if (!arg) return { ok: false, message: `${head}: C needs a heading, altitude or fix` };
        if (/^\d{3}$/.test(arg)) {
          const h = Number(arg);
          if (h < 1 || h > 360) return { ok: false, message: `${head}: heading ${arg} must be 001-360` };
          instructions.push({ kind: 'heading', headingDeg: h % 360 || 360, turn: turnSuffix() });
        } else if (/^\d{1,2}$/.test(arg)) {
          const alt = Number(arg) * 1000;
          if (alt === 0) return { ok: false, message: `${head}: altitude must be at least 1 (1 000 ft)` };
          let expedite = false;
          if (peek() === 'X' || peek() === 'EX') { expedite = true; i++; }
          instructions.push({ kind: 'altitude', altitudeFt: alt, expedite });
        } else if (FIX.test(arg)) {
          instructions.push({ kind: 'direct', fix: arg, turn: turnSuffix() });
        } else return { ok: false, message: `${head}: "${arg}" is not a heading (3 digits), altitude (1-2 digits) or fix` };
        break;
      }
      case 'L': {
        const rw = toks[i++];
        if (!rw || !RUNWAY.test(rw)) return { ok: false, message: `${head}: L needs a runway, e.g. L 28R` };
        instructions.push({ kind: 'land', runway: rw });
        break;
      }
      case 'S': {
        const s = toks[i++];
        if (!s || !/^\d{2,3}$/.test(s)) return { ok: false, message: `${head}: S needs a speed in knots, e.g. S 210` };
        instructions.push({ kind: 'speed', speedKt: Number(s) });
        break;
      }
      case 'H': {
        const f = toks[i++];
        if (!f || !FIX.test(f)) return { ok: false, message: `${head}: H needs a fix, e.g. H OBK` };
        instructions.push({ kind: 'hold', fix: f });
        break;
      }
      case 'T': instructions.push({ kind: 'takeoff' }); break;
      case 'W': case 'LW': instructions.push({ kind: 'lineUp' }); break;
      case 'A': instructions.push({ kind: 'abort' }); break;
      case 'I': {
        const rw = toks[i++];
        if (!rw || !RUNWAY.test(rw)) return { ok: false, message: `${head}: I needs a runway, e.g. I 28R` };
        instructions.push({ kind: 'approach', runway: rw });
        break;
      }
      case 'RN': instructions.push({ kind: 'resumeNav' }); break;
      case 'CT': instructions.push({ kind: 'contact' }); break;
      default: return { ok: false, message: `${head}: unknown command "${kw}" (use C, L, I, S, H, T, W, A, RN, CT)` };
    }
  }
  return { ok: true, command: { type: 'aircraft', callsign: head, instructions } };
}

export const HELP_TEXT = `Commands: ID C 035 (heading) · ID C 12 [EX] (altitude ×1000, expedite) · ID C FIX [L|R] (direct)
ID L 28R (land) · ID I 28R (ILS approach, then L to land) · ID S 210 (speed) · ID H FIX (hold) · ID RN (resume own navigation)
ID W (line up) · ID T (take off) · ID A (abort / go around) · ID CT (contact departure) · ID CTW (Academy approach to tower) · ID RC (radar contact / roger)
Chain: ID C 5 C FIX T.  System: SCALE, STATS, PAUSE, SPEED n, REALISM low|mid|high, AIRPORT ICAO, HELP
Display: VIEW radar|split|tower|pip (Alt+1-4), FULLSCREEN (Alt+F), strips and radio panel (Alt+S), menu (Esc), BRIEF (airport charts)
Map: PROC [active|all|off] (STARs and approach on the scope, Alt+P), TRAIL (selected aircraft's track and route ahead, Alt+T)`;
